import useWebexStore from '@/apps/webexMeet/store/webex.store';
import { isPostHogEnabled } from '@host/config/posthog';
import { trackEvent } from '@host/utils/posthog-track';

// Webex chat open/close is chat_opened/chat_closed with chat_type 'webex'.

let joinStartedAt: number | null = null;
let meetingJoinedAt: number | null = null;
let screenShareStartedAt: number | null = null;
let captionsStartedAt: number | null = null;
let recordingStartedAt: number | null = null;

const secondsSince = (start: number | null) =>
  start === null ? null : Math.round((Date.now() - start) / 1000);

const beacon = (pageUnload: boolean) =>
  pageUnload ? { transport: 'sendBeacon' as const } : undefined;

const endMeeting = (pageUnload = false, leaveReason = 'left') => {
  if (meetingJoinedAt === null) return;
  const duration = secondsSince(meetingJoinedAt);
  meetingJoinedAt = null;
  trackEvent(
    'webex_meeting_left',
    {
      duration_seconds: duration,
      // A restart (e.g. to finalise a recording) ejects everyone and rejoins them.
      is_restart: useWebexStore.getState().isRestarting,
      leave_reason: pageUnload ? 'page_unload' : leaveReason,
    },
    beacon(pageUnload)
  );
};

/** In a live meeting: expiry keeps isInMeeting true (to keep the grid up) but drops `meeting`. */
const isLive = (state: { isInMeeting: boolean; meeting: unknown }) =>
  state.isInMeeting && !!state.meeting;

/** Only this user's own share; isScreenSharing is also true while viewing someone else's. */
export const trackWebexScreenShareStarted = () => {
  if (screenShareStartedAt !== null) return;
  screenShareStartedAt = Date.now();
  trackEvent('webex_screen_share_started');
};

export const trackWebexScreenShareStopped = () => endScreenShare();

const endScreenShare = (pageUnload = false) => {
  if (screenShareStartedAt === null) return;
  const duration = secondsSince(screenShareStartedAt);
  screenShareStartedAt = null;
  trackEvent('webex_screen_share_stopped', { duration_seconds: duration }, beacon(pageUnload));
};

/** Only for captions this user switched on or off, not ones mirrored from another participant. */
export const trackWebexCaptionsToggled = (active: boolean) => {
  if (active) {
    captionsStartedAt = Date.now();
    trackEvent('webex_captions_started');
  } else if (captionsStartedAt !== null) {
    trackEvent('webex_captions_stopped', { duration_seconds: secondsSince(captionsStartedAt) });
    captionsStartedAt = null;
  }
};

export const trackWebexRecordingToggled = (recording: boolean) => {
  if (recording) {
    recordingStartedAt = Date.now();
    trackEvent('webex_recording_started');
  } else {
    trackEvent('webex_recording_stopped', { duration_seconds: secondsSince(recordingStartedAt) });
    recordingStartedAt = null;
  }
};

/** Watches the Webex store: joining and leaving go through several paths (join, hang-up, expiry, restart). */
export const registerWebexTracking = () => {
  if (!isPostHogEnabled) return;

  useWebexStore.subscribe((state, prev) => {
    if (state.isStartingMeeting && !prev.isStartingMeeting) joinStartedAt = Date.now();

    const live = isLive(state);
    const wasLive = isLive(prev);
    if (live && !wasLive) {
      endMeeting();
      meetingJoinedAt = Date.now();
      trackEvent('webex_meeting_joined', {
        is_guest: state.isGuestSession,
        join_ms: joinStartedAt === null ? null : Date.now() - joinStartedAt,
      });
      joinStartedAt = null;
    } else if (!live && wasLive) {
      endScreenShare();
      const leaveReason = state.isInMeeting ? 'expired' : 'left';
      // An ejection flags the restart only after dropping isInMeeting, in the same tick, so
      // read is_restart once that code has run.
      queueMicrotask(() => endMeeting(false, leaveReason));
    }
  });

  window.addEventListener('pagehide', () => {
    endScreenShare(true);
    endMeeting(true);
  });
};
